import { useEffect, useState } from 'react';
import { IoMdClose } from 'react-icons/io';

import Button from '../custom/Button';
import { DataState } from './../../context/DataProvider';
import { authenticateApp } from '../../utils/auth';
import { authApps } from '../../utils/constants';

const TriggerModal = ({
  title,
  desc,
  isOpen,
  onClose,
  appsData,
  setSelectedOptions,
  selectedOptions,
  editOption,
  botCreationId,
}) => {
  const [localSelectedOption, setLocalSelectedOption] = useState(null);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [triggerPhrase, setTriggerPhrase] = useState('');
  const { userGoogleId } = DataState();

  useEffect(() => {
    setLocalSelectedOption(null);
    if (editOption) {
      setLocalSelectedOption(editOption);
      if (editOption.value === 'when_i_message_the_assistant') {
        setTriggerPhrase(editOption?.triggerPhrase || '');
      } else if (editOption.selectedFunctions) {
        setSelectedFunctions([...editOption.selectedFunctions]);
      }
    }
  }, [editOption]);

  const handleOptionSelect = (option) => {
    setLocalSelectedOption(option);
    setTriggerPhrase(''); // Reset trigger phrase when selecting a different app
    setSelectedFunctions([]); // Reset functions if selecting a different app
  };

  const handleConfirm = async () => {
    if (localSelectedOption) {
      let functionsToSave = selectedFunctions;

      // If no functions are selected, select all functions by default
      if (localSelectedOption.functions && selectedFunctions.length === 0) {
        functionsToSave = [...localSelectedOption.functions];
      }

      const optionToSave =
        localSelectedOption.value === 'when_i_message_the_assistant'
          ? { ...localSelectedOption, triggerPhrase }
          : { ...localSelectedOption, selectedFunctions: functionsToSave };

      const updateAuthApps = () => {
        setSelectedOptions((prev) => {
          if (editOption) {
            return prev.map((item) =>
              item.id === editOption.id ? optionToSave : item,
            );
          }
          return [...prev, optionToSave];
        });
      };

      if (authApps.includes(localSelectedOption.value)) {
        await authenticateApp(
          localSelectedOption.value,
          // botCreationId,
          userGoogleId,
          closeModal,
          updateAuthApps,
        );
      } else {
        updateAuthApps();
        closeModal();
      }
    }
  };

  const closeModal = () => {
    setLocalSelectedOption(null);
    setSelectedFunctions([]);
    setTriggerPhrase('');
    onClose();
  };

  return (
    <>
      {isOpen && (
        <>
          {/* eslint-disable-next-line */}
          <div
            className="fixed inset-0 z-[110] bg-black bg-opacity-50"
            onClick={closeModal}
          />
          <div className="bg-2 br-1 text-p-text fixed left-[50%] top-[50%] z-[120] flex max-h-[min(680px,calc(100vh-2rem))] min-h-96 w-[864px] max-w-[calc(100vw-2rem)] translate-x-[-50%] translate-y-[-50%] flex-col rounded-lg border p-8 py-6 shadow-2xl">
            <div className="flex min-h-0 flex-1 flex-col gap-6">
              <header className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <h2 className="p-text text-lg font-semibold">{title}</h2>
                  <p className="text-s-text text-sm">{desc}</p>
                </div>
                <button
                  onClick={closeModal}
                  type="button"
                  className="text-md rounded-[3px] text-xl font-semibold transition-colors duration-150 ease-in-out disabled:cursor-not-allowed"
                >
                  <IoMdClose />
                </button>
              </header>

              <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                {!localSelectedOption?.functions &&
                  localSelectedOption?.value !==
                    'when_i_message_the_assistant' && (
                    <div className="mb-4 flex flex-wrap gap-4">
                      {appsData.map((item) => (
                        <button
                          type="button"
                          onClick={() => handleOptionSelect(item)}
                          key={item.id}
                          className={`text-md flex items-center gap-2 rounded-full border border-zinc-400 px-3 py-2 text-left align-middle font-semibold transition-colors duration-150 ease-in-out ${
                            localSelectedOption?.id === item.id
                              ? 'bg-zinc-200 text-zinc-800'
                              : selectedOptions.some(
                                    (opt) => opt.id === item.id,
                                  )
                                ? 'cursor-not-allowed opacity-50'
                                : ''
                          }`}
                          disabled={
                            selectedOptions.some((opt) => opt.id === item.id) &&
                            !editOption
                          }
                        >
                          <div className="rounded-lg bg-zinc-200 p-1">
                            <img
                              alt={item.label}
                              src={item.image}
                              className="h-7 w-7"
                            />
                          </div>
                          <div className="text-s-text ml-1 text-lg">
                            {item.label}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                {localSelectedOption?.value ===
                  'when_i_message_the_assistant' && (
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="enter-the-trigger-phrase"
                      className="text-sm "
                    >
                      Enter the trigger phrase:
                    </label>
                    <input
                      type="text"
                      value={triggerPhrase}
                      onChange={(e) => setTriggerPhrase(e.target.value)}
                      className="bg-1 rounded-md p-2 outline-none"
                    />
                  </div>
                )}

                {/* Show triggerEvents if available */}
                {localSelectedOption && localSelectedOption?.triggerEvents && (
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="select-webhook-trigger-events"
                      className="text-sm "
                    >
                      Select Webhook Trigger Events
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {localSelectedOption.triggerEvents.map((trigger) => (
                        <button
                          key={trigger.id}
                          title={trigger.description}
                          className={`text-s-text } rounded-full bg-zinc-800 px-3 py-1.5 text-sm 
                                text-zinc-100 transition-all duration-200 hover:bg-zinc-700
                             hover:text-zinc-100`}
                          // onClick={() => handleTriggerEventSelect(trigger.event)}
                        >
                          <span className="text-sm">{trigger.displayName}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show functions if they exist for the selected app */}
                {localSelectedOption &&
                  localSelectedOption?.functions &&
                  localSelectedOption?.functions.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <label htmlFor="select-functions" className="text-sm ">
                        Select functions
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {localSelectedOption.functions.map((func) => (
                          <button
                            key={func}
                            className={`text-s-text rounded-full px-3 py-1.5 text-sm transition-all duration-200 ${
                              selectedFunctions.includes(func)
                                ? 'bg-zinc-100 text-zinc-800 hover:bg-zinc-300'
                                : 'bg-zinc-800 text-zinc-100 hover:bg-zinc-700 hover:text-zinc-100'
                            }`}
                            onClick={() => {
                              if (selectedFunctions.includes(func)) {
                                // Deselect the function
                                setSelectedFunctions(
                                  selectedFunctions.filter((f) => f !== func),
                                );
                              } else {
                                // Select the function
                                setSelectedFunctions([
                                  ...selectedFunctions,
                                  func,
                                ]);
                              }
                            }}
                          >
                            <span className="text-sm">
                              {func.charAt(0).toUpperCase() +
                                func.slice(1).toLowerCase()}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
              </div>

              {/* Show confirm button only when there is a selection */}
              {(localSelectedOption?.functions ||
                localSelectedOption?.value === 'when_i_message_the_assistant' ||
                localSelectedOption) && (
                <footer className="flex justify-end gap-4">
                  <div className="w-28">
                    <Button onClick={handleConfirm}>Confirm</Button>
                  </div>
                </footer>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default TriggerModal;
